import { pdf } from "@react-pdf/renderer";
import React from "react";
import {
  TreatmentPlanResponse,
  PublicTreatmentPlanResponse,
} from "../services/treatmentPlanService";
import TreatmentPlanPdf from "../components/TreatmentPlanPdf/TreatmentPlanPdf";

// Type assertion helper for pdf() function
const createPdf = (
  element: React.ReactElement,
): ReturnType<typeof pdf> => pdf(element);

interface DownloadPdfParams {
  treatmentPlan: TreatmentPlanResponse;
  clinicName?: string;
  clinicEmail?: string;
  clinicPhone?: string;
  clinicWebsite?: string;
}

interface GenerateSignedPdfParams {
  treatmentPlan: PublicTreatmentPlanResponse;
  signature: string;
  signatureMethod: "click" | "draw";
  clinicName?: string;
  clinicEmail?: string;
  clinicPhone?: string;
  clinicWebsite?: string;
}

interface GeneratedPdfResult {
  pdfFileBase64: string;
  pdfFileName: string;
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const blobToBase64 = async (blob: Blob): Promise<string> => {
  const arrayBuffer = await blob.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);
  let binary = "";
  for (let i = 0; i < uint8Array.length; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary);
};

/**
 * Download treatment plan as PDF
 */
export const downloadTreatmentPlanPdf = async ({
  treatmentPlan,
  clinicName,
  clinicEmail,
  clinicPhone,
  clinicWebsite,
}: DownloadPdfParams): Promise<void> => {
  // Build patient name for filename
  const patientName =
    [treatmentPlan.patientFirstName, treatmentPlan.patientLastName]
      .filter(Boolean)
      .join(" ") || "Patient";

  // Clinic info (hardcoded)
  const finalClinicName = clinicName || "Advanced Oral Specialty Group";
  const finalClinicEmail = clinicEmail || "Office@aosgdental.com";
  const finalClinicPhone = clinicPhone || "(856) 880-7558, (856) 830-6469";
  const finalClinicWebsite = clinicWebsite || "advancedoralspecialtygroup.com";

  // Create PDF document
  const pdfDocument = React.createElement(TreatmentPlanPdf, {
    treatmentPlan,
    clinicName: finalClinicName,
    clinicEmail: finalClinicEmail,
    clinicPhone: finalClinicPhone,
    clinicWebsite: finalClinicWebsite,
  });

  // Generate PDF blob
  const blob = await createPdf(pdfDocument).toBlob();

  // Create download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `TreatmentPlan_${patientName.replace(/\s+/g, "_")}_${formatDate(treatmentPlan.dateGenerated).replace(/\//g, "-")}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generate a treatment plan PDF and return as base64 for provider approval
 */
export const generateTreatmentPlanPdfBase64 = async ({
  treatmentPlan,
  clinicName,
  clinicEmail,
  clinicPhone,
  clinicWebsite,
}: DownloadPdfParams): Promise<GeneratedPdfResult> => {
  // Build patient name for filename
  const patientName =
    [treatmentPlan.patientFirstName, treatmentPlan.patientLastName]
      .filter(Boolean)
      .join(" ") || "Patient";

  // Clinic info (hardcoded)
  const finalClinicName = clinicName || "Advanced Oral Specialty Group";
  const finalClinicEmail = clinicEmail || "Office@aosgdental.com";
  const finalClinicPhone = clinicPhone || "(856) 880-7558, (856) 830-6469";
  const finalClinicWebsite = clinicWebsite || "advancedoralspecialtygroup.com";

  // Create PDF document
  const pdfDocument = React.createElement(TreatmentPlanPdf, {
    treatmentPlan,
    clinicName: finalClinicName,
    clinicEmail: finalClinicEmail,
    clinicPhone: finalClinicPhone,
    clinicWebsite: finalClinicWebsite,
  });

  // Generate PDF blob and convert to base64
  const blob = await createPdf(pdfDocument).toBlob();
  const pdfBase64 = await blobToBase64(blob);

  // Generate filename
  const fileName = `TreatmentPlan_${patientName.replace(/\s+/g, "_")}_${formatDate(treatmentPlan.dateGenerated).replace(/\//g, "-")}.pdf`;

  return {
    pdfFileBase64: pdfBase64,
    pdfFileName: fileName,
  };
};

/**
 * Generate a signed PDF for patient approval and return as base64
 */
export const generateSignedTreatmentPlanPdf = async ({
  treatmentPlan,
  signature,
  signatureMethod,
  clinicName,
  clinicEmail,
  clinicPhone,
  clinicWebsite,
}: GenerateSignedPdfParams): Promise<GeneratedPdfResult> => {
  // Build patient name for filename
  const patientName =
    [treatmentPlan.patientFirstName, treatmentPlan.patientLastName]
      .filter(Boolean)
      .join(" ") || "Patient";

  // Clinic info (hardcoded)
  const finalClinicName = clinicName || "Advanced Oral Specialty Group";
  const finalClinicEmail = clinicEmail || "Office@aosgdental.com";
  const finalClinicPhone = clinicPhone || "(856) 880-7558, (856) 830-6469";
  const finalClinicWebsite = clinicWebsite || "advancedoralspecialtygroup.com";

  // Create PDF document with signature
  const pdfDocument = React.createElement(TreatmentPlanPdf, {
    treatmentPlan,
    clinicName: finalClinicName,
    clinicEmail: finalClinicEmail,
    clinicPhone: finalClinicPhone,
    clinicWebsite: finalClinicWebsite,
    signature,
    signatureMethod,
  });

  // Generate PDF blob and convert to base64
  const blob = await createPdf(pdfDocument).toBlob();
  const pdfBase64 = await blobToBase64(blob);

  // Generate filename
  const fileName = `TreatmentPlan_${patientName.replace(/\s+/g, "_")}_${formatDate(treatmentPlan.dateGenerated).replace(/\//g, "-")}_signed.pdf`;

  return {
    pdfFileBase64: pdfBase64,
    pdfFileName: fileName,
  };
};
